import { clearToken, getToken } from './auth-storage.ts';
import { API_URL } from './env.ts';
import type { ApiResponse } from '../types/api-response.ts';

/**
 * 共通 fetch wrapper。
 *
 * - Authorization: Bearer <token> を自動付与
 * - 401 で localStorage クリア + `/login` リダイレクト（無限ループ防止のため auth 系は除外）
 *
 * 注意: SSO 受け口（/auth/portal-code）の呼び出しは `portal-login.ts` の専用 fetch を使うこと。
 * api-client を使うと 401 でリダイレクト → 失敗の一言が画面に出せずデバッグ不能になる。
 */

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const isAuthEndpoint = (path: string): boolean =>
  path.startsWith('/auth/portal') || path.startsWith('/auth/me');

const redirectToLogin = (): void => {
  // history 汚染防止 + 既に /login にいるなら再遷移しない
  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

export const request = async <T>(
  path: string,
  options: RequestOptions = {}
): Promise<T> => {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const fetchInit: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.body !== undefined) fetchInit.body = JSON.stringify(options.body);
  if (options.signal) fetchInit.signal = options.signal;

  const res = await fetch(`${API_URL}${path}`, fetchInit);

  if (res.status === 401 && !isAuthEndpoint(path)) {
    clearToken();
    redirectToLogin();
    throw new ApiError('unauthorized', 'session expired', 401);
  }

  const json = (await res.json()) as ApiResponse<T>;
  if ('ok' in json && json.ok === true) return json.data;

  const errorRes = json as { error?: { code: string; message: string } };
  throw new ApiError(
    errorRes.error?.code ?? 'unknown',
    errorRes.error?.message ?? 'unknown error',
    res.status
  );
};
